import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Button,
  Form,
  Card,
  Row,
  Col,
  Alert,
  ListGroup,
} from "react-bootstrap";
import { db, auth } from "../firebase";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import moment from "moment-timezone";
import "./RoomBookingForm.css";
import {
  formatDateGMT7,
  getTodayGMT7,
  weekDays,
  checkTimeSlotAvailability,
} from "../utils";
import { sendEmailToSingleEmail } from "../emailUtils";
import BookingConfirmationModal from "./modals/BookingConfirmationModal";
import BookingConflictModal from "./modals/BookingConflictModal";
import InvitationConfirmationModal from "./modals/InvitationConfirmationModal";
import DeleteConfirmationModal from "./modals/DeleteConfirmationModal";
import BookingEmailTemplate from "./BookingEmailTemplate";
import Flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { getAdminEmails } from "../data/getAdminEmails";

const RoomBookingForm = ({
  selectedRoom,
  setBookings,
  onReset,
  editingBooking,
  selectedSlot,
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [purpose, setPurpose] = useState("");
  const [error, setError] = useState(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState("weekly");
  const [recurrenceEndDate, setRecurrenceEndDate] = useState("");
  const [recurrenceDays, setRecurrenceDays] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showOverlapModal, setShowOverlapModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [overlapError, setOverlapError] = useState("");
  const [pendingBooking, setPendingBooking] = useState(null);
  const [userBookings, setUserBookings] = useState([]);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [internalEditingBooking, setInternalEditingBooking] = useState(null);

  const datePickerRef = useRef(null);
  const recurrenceEndDatePickerRef = useRef(null);
  const startTimePickerRef = useRef(null);
  const endTimePickerRef = useRef(null);

  const flatpickrInstances = useRef({
    datePicker: null,
    recurrenceEndDatePicker: null,
    startTimePicker: null,
    endTimePicker: null,
  });

  useEffect(() => {
    onReset(() => resetForm);
  }, [onReset]);

  useEffect(() => {
    if (selectedRoom) {
      resetForm();
    }
  }, [selectedRoom]);

  useEffect(() => {
    if (editingBooking) {
      handleEditBooking(editingBooking);
    }
  }, [editingBooking]);

  useEffect(() => {
    if (selectedSlot && !editingBooking) {
      const slotDate = formatDateGMT7(selectedSlot.start);
      const now = moment().tz("Asia/Ho_Chi_Minh").toDate();
      const slotStartTime = selectedSlot.start.toTimeString().slice(0, 5);
      const slotDateTime = moment
        .tz(
          `${slotDate} ${slotStartTime}`,
          "YYYY-MM-DD HH:mm",
          "Asia/Ho_Chi_Minh"
        )
        .toDate();

      if (slotDateTime < now) {
        setError("Cannot select a date or time in the past");
        return;
      }

      resetForm();
      setDate(slotDate);
      setStartTime(slotStartTime);
      setEmail(selectedSlot.email || auth.currentUser?.email || "");

      if (flatpickrInstances.current.datePicker) {
        flatpickrInstances.current.datePicker.setDate(slotDate);
      }
      if (flatpickrInstances.current.startTimePicker) {
        flatpickrInstances.current.startTimePicker.setDate(slotStartTime);
      }
    }
  }, [selectedSlot, editingBooking]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user && selectedRoom?.docId) {
        setEmail(user.email);
        setName(user.displayName || "");
        await fetchUserBookings(user.email);
      }
    });
    return () => unsubscribe(); // eslint-disable-next-line
  }, [selectedRoom]);

  const fetchUserBookings = async (userEmail) => {
    try {
      const roomRef = doc(db, "rooms", selectedRoom.docId);
      const bookingsRef = collection(roomRef, "bookings");
      const q = query(bookingsRef, where("email", "==", userEmail));
      const querySnapshot = await getDocs(q);
      const bookings = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUserBookings(bookings);
    } catch (err) {
      console.error("Error fetching user bookings:", err);
      setError("Failed to load your bookings");
    }
  };

  useEffect(() => {
    if (datePickerRef.current && !flatpickrInstances.current.datePicker) {
      flatpickrInstances.current.datePicker = Flatpickr(datePickerRef.current, {
        dateFormat: "Y-m-d",
        minDate: formatDateGMT7(moment().tz("Asia/Ho_Chi_Minh").toDate()),
        onChange: ([selectedDate]) => {
          if (selectedDate) {
            handleDateChange({
              target: { value: formatDateGMT7(selectedDate) },
            });
          }
        },
      });
    }

    if (
      startTimePickerRef.current &&
      !flatpickrInstances.current.startTimePicker
    ) {
      flatpickrInstances.current.startTimePicker = Flatpickr(
        startTimePickerRef.current,
        {
          enableTime: true,
          noCalendar: true,
          dateFormat: "H:i",
          time_24hr: true,
          onOpen: () => {
            const now = moment().tz("Asia/Ho_Chi_Minh");
            if (date === formatDateGMT7(now.toDate())) {
              flatpickrInstances.current.startTimePicker.set(
                "minTime",
                now.format("H:i")
              );
            } else {
              flatpickrInstances.current.startTimePicker.set("minTime", false);
            }
          },
          onChange: ([selectedTime]) => {
            if (selectedTime) {
              const timeStr = selectedTime.toTimeString().slice(0, 5);
              const selectedDateTime = moment
                .tz(
                  `${date} ${timeStr}`,
                  "YYYY-MM-DD HH:mm",
                  "Asia/Ho_Chi_Minh"
                )
                .toDate();
              const now = moment().tz("Asia/Ho_Chi_Minh").toDate();
              if (selectedDateTime < now) {
                setError("Start time cannot be in the past");
                return;
              }
              setStartTime(timeStr);
              setError(null);
            }
          },
        }
      );
    }

    if (endTimePickerRef.current && !flatpickrInstances.current.endTimePicker) {
      flatpickrInstances.current.endTimePicker = Flatpickr(
        endTimePickerRef.current,
        {
          enableTime: true,
          noCalendar: true,
          dateFormat: "H:i",
          time_24hr: true,
          onOpen: () => {
            if (startTime && date) {
              const now = moment().tz("Asia/Ho_Chi_Minh");
              const isToday = date === formatDateGMT7(now.toDate());
              const minTime = isToday
                ? startTime > now.format("H:i")
                  ? startTime
                  : now.format("H:i")
                : startTime;
              flatpickrInstances.current.endTimePicker.set("minTime", minTime);
            } else {
              flatpickrInstances.current.endTimePicker.set("minTime", false);
            }
          },
          onChange: ([selectedTime]) => {
            if (selectedTime) {
              const timeStr = selectedTime.toTimeString().slice(0, 5);
              setEndTime(timeStr);
            }
          },
        }
      );
    }

    return () => {
      Object.values(flatpickrInstances.current).forEach((instance) => {
        if (instance) {
          instance.destroy();
        }
      });
      flatpickrInstances.current = {
        datePicker: null,
        recurrenceEndDatePicker: null,
        startTimePicker: null,
        endTimePicker: null,
      };
    }; // eslint-disable-next-line
  }, [date, startTime]);

  useEffect(() => {
    if (
      isRecurring &&
      recurrenceEndDatePickerRef.current &&
      !flatpickrInstances.current.recurrenceEndDatePicker
    ) {
      const minDate = date ? new Date(date) : new Date();
      minDate.setDate(minDate.getDate() + 1);
      flatpickrInstances.current.recurrenceEndDatePicker = Flatpickr(
        recurrenceEndDatePickerRef.current,
        {
          dateFormat: "Y-m-d",
          minDate: formatDateGMT7(minDate),
          onChange: ([selectedDate]) => {
            if (selectedDate) {
              handleDateChange(
                { target: { value: formatDateGMT7(selectedDate) } },
                true
              );
            }
          },
        }
      );
    }

    return () => {
      if (!isRecurring && flatpickrInstances.current.recurrenceEndDatePicker) {
        flatpickrInstances.current.recurrenceEndDatePicker.destroy();
        flatpickrInstances.current.recurrenceEndDatePicker = null;
      }
    }; // eslint-disable-next-line
  }, [isRecurring, date]);

  const checkTimeSlotAvailabilityWrapper = async (bookingId = null) => {
    if (!date || !startTime || !endTime || !selectedRoom) {
      setError("Please fill in all required fields");
      return false;
    }

    const now = moment().tz("Asia/Ho_Chi_Minh").toDate();
    const selectedDateTime = moment
      .tz(`${date} ${startTime}`, "YYYY-MM-DD HH:mm", "Asia/Ho_Chi_Minh")
      .toDate();

    if (selectedDateTime < now) {
      setError("Start time cannot be in the past");
      return;
    }

    const booking = {
      date,
      startTime,
      endTime,
      isRecurring,
      recurrenceType,
      recurrenceEndDate,
      recurrenceDays,
    };

    try {
      await checkTimeSlotAvailability(
        booking,
        selectedRoom.docId,
        db,
        bookingId
      );
      return true;
    } catch (err) {
      setOverlapError(err.message);
      setShowOverlapModal(true);
      return false;
    }
  };

  const handleRecurrenceDayChange = useCallback((dayId) => {
    setRecurrenceDays((prev) => {
      if (prev.includes(dayId)) {
        return prev.filter((id) => id !== dayId);
      } else {
        return [...prev, dayId];
      }
    });
  }, []);

  const handleDateChange = useCallback(
    (e, isRecurrenceEndDate = false) => {
      const selectedDate = new Date(e.target.value);
      const today = getTodayGMT7();

      if (selectedDate < today) {
        setError(
          isRecurrenceEndDate
            ? "Recurrence end date cannot be in the past"
            : "Cannot book for past dates"
        );
        return;
      }

      if (isRecurrenceEndDate && date && new Date(date) >= selectedDate) {
        setError("Recurrence end date must be after the booking date");
        return;
      }

      const formattedDate = formatDateGMT7(selectedDate);
      if (isRecurrenceEndDate) {
        setRecurrenceEndDate(formattedDate);
      } else {
        setDate(formattedDate);
        if (recurrenceEndDate && new Date(recurrenceEndDate) <= selectedDate) {
          setRecurrenceEndDate("");
        }
      }
      setError(null);
    },
    [date, recurrenceEndDate]
  );

  const sendBookingEmails = async (booking, isUpdate = false) => {
    const action = isUpdate ? "Update" : "Confirmation";
    const bookerSubject = `Booking ${action} for ${booking.room.name}`;
    const adminSubject = `${isUpdate ? "Updated" : "New"} Booking for ${
      booking.room.name
    }`;
    const emailContent = BookingEmailTemplate({
      booking: { ...booking, updated: isUpdate },
    });

    const bookerSent = await sendEmailToSingleEmail(
      booking.email,
      bookerSubject,
      emailContent,
      booking.name
    );

    const adminEmails = await getAdminEmails();

    const adminResults = await Promise.all(
      adminEmails.map(async (adminEmail) => {
        return sendEmailToSingleEmail(
          adminEmail,
          adminSubject,
          emailContent,
          "Admin"
        );
      })
    );

    if (!bookerSent) {
      console.warn("Failed to send email to booker");
      setError(
        `Booking ${
          isUpdate ? "updated" : "successful"
        }, but failed to send ${action.toLowerCase()} email to you.`
      );
    }
    adminResults.forEach((success, index) => {
      if (!success) {
        console.warn(`Failed to send email to admin ${adminEmails[index]}`);
      }
    });
  };

  const resetForm = () => {
    setName(auth.currentUser?.displayName || "");
    setEmail(auth.currentUser?.email || "");
    setDate("");
    setStartTime("");
    setEndTime("");
    setPurpose("");
    setIsRecurring(false);
    setRecurrenceType("weekly");
    setRecurrenceEndDate("");
    setRecurrenceDays([]);
    setError(null);
    setShowOverlapModal(false);
    setOverlapError("");
    setPendingBooking(null);
    setShowConfirmationModal(false);
    setShowInvitationModal(false);
    setInternalEditingBooking(null);

    if (flatpickrInstances.current.datePicker) {
      flatpickrInstances.current.datePicker.clear();
    }
    if (flatpickrInstances.current.startTimePicker) {
      flatpickrInstances.current.startTimePicker.clear();
    }
    if (flatpickrInstances.current.endTimePicker) {
      flatpickrInstances.current.endTimePicker.clear();
    }
    if (flatpickrInstances.current.recurrenceEndDatePicker) {
      flatpickrInstances.current.recurrenceEndDatePicker.clear();
    }
  };

  const handleEditBooking = (booking) => {
    setInternalEditingBooking(booking);
    setName(booking.name);
    setEmail(booking.email);
    setDate(booking.date);
    setStartTime(booking.startTime);
    setEndTime(booking.endTime);
    setPurpose(booking.purpose);
    setIsRecurring(booking.isRecurring);
    setRecurrenceType(booking.recurrenceType || "weekly");
    setRecurrenceEndDate(booking.recurrenceEndDate || "");
    setRecurrenceDays(booking.recurrenceDays || []);

    if (flatpickrInstances.current.datePicker) {
      flatpickrInstances.current.datePicker.setDate(booking.date);
    }
    if (flatpickrInstances.current.startTimePicker) {
      flatpickrInstances.current.startTimePicker.setDate(booking.startTime);
    }
    if (flatpickrInstances.current.endTimePicker) {
      flatpickrInstances.current.endTimePicker.setDate(booking.endTime);
    }
    if (
      booking.isRecurring &&
      booking.recurrenceEndDate &&
      flatpickrInstances.current.recurrenceEndDatePicker
    ) {
      flatpickrInstances.current.recurrenceEndDatePicker.setDate(
        booking.recurrenceEndDate
      );
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    const booking = userBookings.find((b) => b.id === bookingId);
    if (booking) {
      setBookingToDelete(booking);
      setShowDeleteModal(true);
    }
  };

  const confirmDeleteBooking = async () => {
    if (!bookingToDelete) return;

    try {
      const roomRef = doc(db, "rooms", selectedRoom.docId);
      const bookingRef = doc(roomRef, "bookings", bookingToDelete.id);
      await deleteDoc(bookingRef);

      setUserBookings((prev) =>
        prev.filter((booking) => booking.id !== bookingToDelete.id)
      );
      setBookings((prev) =>
        prev.filter((booking) => booking.id !== bookingToDelete.id)
      );

      const bookerSubject = `Booking Cancellation for ${bookingToDelete.room}`;
      const adminSubject = `Booking Cancellation for ${bookingToDelete.room}`;
      const emailContent = BookingEmailTemplate({
        booking: {
          ...bookingToDelete,
          cancelled: true,
          room: { name: bookingToDelete.room },
        },
      });

      await sendEmailToSingleEmail(
        bookingToDelete.email,
        bookerSubject,
        emailContent,
        bookingToDelete.name
      );

      const adminEmails = await getAdminEmails();

      await Promise.all(
        adminEmails.map((adminEmail) =>
          sendEmailToSingleEmail(
            adminEmail,
            adminSubject,
            emailContent,
            "Admin"
          )
        )
      );
      resetForm();
    } catch (err) {
      console.error("Error deleting booking:", err);
      setError("Failed to delete booking");
    } finally {
      setShowDeleteModal(false);
      setBookingToDelete(null);
    }
  };

  const cancelDeleteBooking = () => {
    setShowDeleteModal(false);
    setBookingToDelete(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Invalid email address");
      setIsSubmitting(false);
      return;
    }

    if (
      isRecurring &&
      recurrenceType === "weekly" &&
      recurrenceDays.length === 0
    ) {
      setError("Please select at least one day for weekly recurrence");
      setIsSubmitting(false);
      return;
    }

    if (
      isRecurring &&
      (!recurrenceEndDate || new Date(date) >= new Date(recurrenceEndDate))
    ) {
      setError("Recurrence end date must be after the booking start date");
      setIsSubmitting(false);
      return;
    }

    const now = moment().tz("Asia/Ho_Chi_Minh").toDate();
    const selectedDateTime = moment
      .tz(`${date} ${startTime}`, "YYYY-MM-DD HH:mm", "Asia/Ho_Chi_Minh")
      .toDate();

    if (selectedDateTime < now) {
      setError("Start time cannot be in the past");
      setIsSubmitting(false);
      return;
    }

    let isAvailable = true;
    if (internalEditingBooking) {
      const hasChanged =
        name !== internalEditingBooking.name ||
        date !== internalEditingBooking.date ||
        startTime !== internalEditingBooking.startTime ||
        endTime !== internalEditingBooking.endTime ||
        purpose !== internalEditingBooking.purpose ||
        isRecurring !== internalEditingBooking.isRecurring ||
        recurrenceType !==
          (internalEditingBooking.recurrenceType || "weekly") ||
        recurrenceEndDate !==
          (internalEditingBooking.recurrenceEndDate || "") ||
        JSON.stringify(recurrenceDays.sort()) !==
          JSON.stringify((internalEditingBooking.recurrenceDays || []).sort());

      if (!hasChanged) {
        setError("No changes made to the booking.");
        setIsSubmitting(false);
        return;
      }

      isAvailable = await checkTimeSlotAvailabilityWrapper(
        internalEditingBooking.id
      );
    } else {
      isAvailable = await checkTimeSlotAvailabilityWrapper();
    }

    if (!isAvailable) {
      setIsSubmitting(false);
      return;
    }

    const baseBooking = {
      name,
      email,
      date,
      startTime,
      endTime,
      purpose,
      room: selectedRoom,
      isRecurring,
      recurrenceType: isRecurring ? recurrenceType : null,
      recurrenceEndDate: isRecurring ? recurrenceEndDate : null,
      recurrenceDays: isRecurring ? recurrenceDays : [],
    };

    try {
      const roomRef = doc(db, "rooms", selectedRoom.docId);
      const bookingsRef = collection(roomRef, "bookings");

      if (internalEditingBooking) {
        const bookingRef = doc(bookingsRef, internalEditingBooking.id);
        await updateDoc(bookingRef, {
          ...baseBooking,
          room: selectedRoom.name,
        });

        setBookings((prevBookings) =>
          prevBookings.map((b) =>
            b.id === internalEditingBooking.id
              ? {
                  ...baseBooking,
                  id: internalEditingBooking.id,
                  room: selectedRoom,
                }
              : b
          )
        );
        setUserBookings((prevBookings) =>
          prevBookings.map((b) =>
            b.id === internalEditingBooking.id
              ? {
                  ...baseBooking,
                  id: internalEditingBooking.id,
                  room: selectedRoom.name,
                }
              : b
          )
        );

        await sendBookingEmails(baseBooking, true);

        setPendingBooking({
          ...baseBooking,
          updated: true,
        });
      } else {
        const docRef = await addDoc(bookingsRef, {
          ...baseBooking,
          room: selectedRoom.name,
        });

        const createdBooking = {
          ...baseBooking,
          id: docRef.id,
          room: selectedRoom,
        };

        setBookings((prevBookings) => [...prevBookings, createdBooking]);
        setUserBookings((prevBookings) => [
          ...prevBookings,
          { ...baseBooking, id: docRef.id, room: selectedRoom.name },
        ]);

        await sendBookingEmails(baseBooking);

        setPendingBooking({
          ...createdBooking,
          updated: false,
        });
      }

      setShowConfirmationModal(true);
    } catch (error) {
      console.error("Error submitting booking:", error);
      setError(
        internalEditingBooking
          ? "Error updating booking"
          : "Error submitting booking"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmationClose = () => {
    setShowConfirmationModal(false);
    if (pendingBooking) {
      setShowInvitationModal(true);
    }
  };

  const handleInvitationConfirm = () => {
    setShowInvitationModal(false);
    setPendingBooking(null);
    resetForm();
  };

  const handleInvitationCancel = () => {
    setShowInvitationModal(false);
    setPendingBooking(null);
    resetForm();
  };

  const handleOverlapModalClose = () => {
    setShowOverlapModal(false);
    setOverlapError("");
  };

  const recurrenceDaysCheckboxes = useMemo(() => {
    return weekDays.map((day) => (
      <Form.Check
        key={day.id}
        type="checkbox"
        id={`day-${day.id}`}
        label={day.name}
        checked={recurrenceDays.includes(day.id)}
        onChange={() => handleRecurrenceDayChange(day.id)}
        inline
      />
    ));
  }, [recurrenceDays, handleRecurrenceDayChange]);

  return (
    <>
      <Card className="booking-form-card">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="form-title mb-0">
            {internalEditingBooking
              ? `Edit Booking for ${selectedRoom.name}`
              : `Book ${selectedRoom.name}`}
          </h4>
          <div>
            {internalEditingBooking && (
              <Button
                variant="outline-danger"
                onClick={() => handleDeleteBooking(internalEditingBooking.id)}
                className="me-2"
              >
                Delete
              </Button>
            )}
            <Button variant="secondary" onClick={resetForm}>
              {internalEditingBooking ? "Cancel Edit" : "Reset"}
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Your Name</Form.Label>
                  <Form.Control
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="Enter your name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type="text"
                    ref={datePickerRef}
                    value={date}
                    placeholder="Select date"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Time</Form.Label>
                  <Form.Control
                    type="text"
                    ref={startTimePickerRef}
                    placeholder="Select start time"
                    value={startTime}
                    readOnly
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Time</Form.Label>
                  <Form.Control
                    type="text"
                    ref={endTimePickerRef}
                    placeholder="Select end time"
                    value={endTime}
                    readOnly
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Your Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    readOnly
                    required
                    disabled
                    placeholder="Your email"
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Purpose</Form.Label>
                  <Form.Control
                    type="text"
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                    placeholder="Enter the purpose of your booking"
                    style={{ width: "100%" }}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="recurring-meeting"
                label="Recurring Meeting"
                checked={isRecurring}
                onChange={(e) => setIsRecurring(e.target.checked)}
              />
            </Form.Group>
            {isRecurring && (
              <>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Recurrence Type</Form.Label>
                      <Form.Select
                        value={recurrenceType}
                        onChange={(e) => setRecurrenceType(e.target.value)}
                        className="form-control"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>End Date</Form.Label>
                      <Form.Control
                        type="text"
                        ref={recurrenceEndDatePickerRef}
                        value={recurrenceEndDate}
                        placeholder="Select end date"
                        required={isRecurring}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {recurrenceType === "weekly" && (
                  <Form.Group className="mb-3">
                    <Form.Label>Repeat On</Form.Label>
                    <div className="day-selector">
                      {recurrenceDaysCheckboxes}
                    </div>
                  </Form.Group>
                )}
              </>
            )}
            <Button
              type="submit"
              className="submit-button"
              size="lg"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? internalEditingBooking
                  ? "Updating..."
                  : "Booking..."
                : internalEditingBooking
                ? "Update Booking"
                : "Book Room"}
            </Button>
          </Form>
        </Card.Body>
      </Card>
      <Card className="booking-form-card mt-4">
        <Card.Header>
          <h4 className="form-title mb-0">Your Bookings</h4>
        </Card.Header>
        <Card.Body>
          {userBookings.length === 0 ? (
            <p>No bookings found.</p>
          ) : (
            <ListGroup>
              {userBookings.map((booking) => (
                <ListGroup.Item
                  key={booking.id}
                  className="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <strong>{booking.purpose}</strong>
                    <br />
                    Date: {booking.date} | Time: {booking.startTime} -{" "}
                    {booking.endTime}
                    {booking.isRecurring && (
                      <>
                        <br />
                        Recurring: {booking.recurrenceType}
                        {booking.recurrenceType === "weekly" && (
                          <>
                            {" on "}
                            {booking.recurrenceDays
                              .map(
                                (dayId) =>
                                  weekDays.find((d) => d.id === dayId)?.name
                              )
                              .join(", ")}
                          </>
                        )}
                        {booking.recurrenceEndDate && (
                          <>
                            {" until "} {booking.recurrenceEndDate}
                          </>
                        )}
                      </>
                    )}
                  </div>
                  <div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleEditBooking(booking)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteBooking(booking.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Card.Body>
      </Card>
      <BookingConflictModal
        show={showOverlapModal}
        onClose={handleOverlapModalClose}
        overlapError={overlapError}
      />
      <InvitationConfirmationModal
        show={showInvitationModal}
        onConfirm={handleInvitationConfirm}
        onCancel={handleInvitationCancel}
        booking={pendingBooking}
      />
      <BookingConfirmationModal
        show={showConfirmationModal}
        onClose={handleConfirmationClose}
        booking={pendingBooking}
      />
      <DeleteConfirmationModal
        show={showDeleteModal}
        onConfirm={confirmDeleteBooking}
        onCancel={cancelDeleteBooking}
        booking={bookingToDelete}
      />
    </>
  );
};

export default RoomBookingForm;
